import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { BadgeVariant } from '@interstate/components/Badge';
import { getLenderCreditSourceLabel } from '@makemydeal/dr-dash-components';
import * as baseDealActionCreators from '@makemydeal/dr-dash-store/actions/baseDealActionCreators';
import * as deskingActionCreators from '@makemydeal/dr-dash-store/actions/deskingActionCreators';
import * as interactionAnalyticsActionCreators from '@makemydeal/dr-dash-store/actions/interactionAnalyticsActionCreators';
import * as deskingSelectors from '@makemydeal/dr-dash-store/selectors/deskingSelectors';
import * as vehicleSelectors from '@makemydeal/dr-dash-store/selectors/vehicleSelectors';
import type { StateTree } from '@makemydeal/dr-dash-types';
import { scopedStateSelectors } from '@makemydeal/shared-scoped-store';

import { useAISectionVisibility } from './useAISectionVisibility';
import { useOnceInView } from './useOnceInView';

export const useAiInsights = ({ scopeId }: { scopeId: string }) => {
    const dispatch = useDispatch();
    const visibility = useAISectionVisibility({ scopeId });
    const { hasLenderProbabilities, lenderApprovalLevel, predictedRate, isAiRecommendationsCTAAvailable } = visibility;

    const shouldShowAIRefreshButton = useSelector(deskingSelectors.getShouldShowAIRefreshButton);
    const vehicleAnalyticsPayload = useSelector(vehicleSelectors.getVehicleAnalyticsPayload);
    const lenderProbabilities = useSelector(deskingSelectors.getLenderProbabilities);
    const lenderCreditSource = useSelector(deskingSelectors.getLenderCreditSource);
    const topLender = useSelector(deskingSelectors.getTopLenderData);
    const currentVehicle = useSelector((state: any) => {
        const scopeState = scopedStateSelectors.getScopeStateById(state, scopeId) as StateTree | undefined;
        return scopeState ? vehicleSelectors.getVehicle(scopeState) : undefined;
    });

    const affordabilityCardInViewRef = useOnceInView(
        useCallback(() => {
            dispatch(interactionAnalyticsActionCreators.lenderApprovalLikelihoodViewed(topLender, currentVehicle));
        }, [dispatch, topLender, currentVehicle])
    );

    const affordabilityStatus: BadgeVariant = lenderApprovalLevel === 'High' ? 'success' : 'light';

    const getAffordabilityMessage = (): string => {
        if (shouldShowAIRefreshButton) {
            return 'Click to view latest recommendations';
        }

        if (!hasLenderProbabilities) {
            return 'Approval likelihood is currently unavailable.';
        }

        return lenderApprovalLevel === 'High'
            ? `At least one lender will approve this deal starting at ${predictedRate}% APR.`
            : 'Limited likelihood of lenders approving this deal as structured.';
    };

    const creditScoreSourceLabel = hasLenderProbabilities
        ? `Credit Score: ${getLenderCreditSourceLabel(lenderCreditSource)}`
        : undefined;

    const recommendationsMessage = isAiRecommendationsCTAAvailable
        ? 'Affordable similar vehicles in inventory with better payment.'
        : 'Alternative Vehicle Options are currently unavailable.';

    const openLenderLikelihood = () => {
        dispatch(baseDealActionCreators.openSlideOut(baseDealActionCreators.SlideOutType.Lender));
        dispatch(interactionAnalyticsActionCreators.clickLenderApprovalLikelihoodLink(topLender, currentVehicle));
    };

    const refreshInsights = () => {
        const lenderAnalytics = lenderProbabilities?.map((lender) => ({
            lenderId: lender.lenderId ?? lender.lender,
            lenderName: lender.lender,
            loanLikelihood: lender.approveProb
        }));
        dispatch(deskingActionCreators.aiRefreshClicked(vehicleAnalyticsPayload, lenderAnalytics));
        dispatch(deskingActionCreators.triggerAIRefresh());
    };

    return {
        ...visibility,
        shouldShowAIRefreshButton,
        affordabilityCardInViewRef,
        affordabilityStatus,
        affordabilityMessage: getAffordabilityMessage(),
        creditScoreSourceLabel,
        recommendationsMessage,
        openLenderLikelihood,
        refreshInsights
    };
};

export type AiInsights = ReturnType<typeof useAiInsights>;
